import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'

interface Report {
  id: string
  report_id: string
  patient: {
    id: string
    patient_id: string
    full_name: string
    date_of_birth: string
    gender: string
  }
  status: 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'FINALIZED'
  diagnosis: string
  pathologist?: {
    username: string
    first_name: string
    last_name: string
  }
  created_at: string
  updated_at: string
  finalized_at?: string
  reviewed_at?: string
  reviewed_by?: {
    username: string
    first_name: string
    last_name: string
  }
}

const ApprovalWorklist: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [filter, setFilter] = useState<'pending' | 'approved' | 'all'>('pending')
  const [selectedReport, setSelectedReport] = useState<Report | null>(null)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    fetchReports()
  }, [])

  const fetchReports = async () => {
    try {
      setIsLoading(true)
      // Fetch cytology reports
      const response = await fetch('/api/v1/pathology/cytology-reports/?page_size=1000')
      if (response.ok) {
        const data = await response.json()
        const cytologyReports = (data.results || data).map((r: any) => ({
          id: r.id,
          report_id: `CYT-${r.id}`,
          patient: {
            id: r.patient,
            patient_id: r.patient_code || '',
            full_name: r.patient_name || '',
            date_of_birth: '',
            gender: r.gender || ''
          },
          status: r.status === 'pending_review' ? 'PENDING_REVIEW' : r.status === 'finalized' ? 'APPROVED' : 'DRAFT',
          diagnosis: r.conclusion || '',
          pathologist: r.cytopathologist ? {
            username: r.cytopathologist,
            first_name: r.cytopathologist.split(' ')[0] || '',
            last_name: r.cytopathologist.split(' ').slice(1).join(' ') || ''
          } : undefined,
          created_at: r.report_date,
          updated_at: r.updated_at,
          finalized_at: r.report_date
        }))
        setReports(cytologyReports)
      }
    } catch (error) {
      console.error('Error fetching reports:', error)
      toast.error('Không thể tải danh sách phiếu kết quả')
    } finally {
      setIsLoading(false)
    }
  }

  const handleApprove = async (report: Report) => {
    if (!window.confirm(`Xác nhận duyệt phiếu kết quả cho bệnh nhân ${report.patient.full_name}?`)) {
      return
    }

    try {
      setActionLoading(true)
      const response = await fetch(`/api/v1/pathology/cytology-reports/${report.id}/finalize/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (response.ok) {
        const result = await response.json()
        toast.success(result.message || 'Đã duyệt phiếu kết quả')
        fetchReports()
      } else {
        const error = await response.json()
        toast.error(error.detail || error.error || 'Không thể duyệt phiếu')
      }
    } catch (error) {
      console.error('Error approving report:', error)
      toast.error('Lỗi khi duyệt phiếu')
    } finally {
      setActionLoading(false)
    }
  }

  const openRejectModal = (report: Report) => {
    setSelectedReport(report)
    setRejectionReason('')
    setShowRejectModal(true)
  }

  const handleReject = async () => {
    if (!selectedReport) return

    if (!rejectionReason.trim()) {
      toast.error('Vui lòng nhập lý do từ chối')
      return
    }

    try {
      setActionLoading(true)
      const response = await fetch(`/api/v1/pathology/cytology-reports/${selectedReport.id}/reject/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: rejectionReason
        })
      })

      if (response.ok) {
        const result = await response.json()
        toast.success(result.message || 'Đã từ chối phiếu kết quả')
        setShowRejectModal(false)
        setSelectedReport(null)
        setRejectionReason('')
        fetchReports()
      } else {
        const error = await response.json()
        toast.error(error.error || error.detail || 'Không thể từ chối phiếu')
      }
    } catch (error) {
      console.error('Error rejecting report:', error)
      toast.error('Lỗi khi từ chối phiếu')
    } finally {
      setActionLoading(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status: string) => {
    const statusMap = {
      DRAFT: { label: 'Nháp', color: 'bg-gray-100 text-gray-800', icon: '📝' },
      PENDING_REVIEW: { label: 'Chờ duyệt', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      APPROVED: { label: 'Đã duyệt', color: 'bg-green-100 text-green-800', icon: '✅' },
      FINALIZED: { label: 'Hoàn tất', color: 'bg-blue-100 text-blue-800', icon: '📋' }
    }
    const info = statusMap[status as keyof typeof statusMap] || statusMap.DRAFT
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${info.color}`}>
        {info.icon} {info.label}
      </span>
    )
  }

  const filteredReports = reports.filter(report => {
    if (filter === 'pending') return report.status === 'PENDING_REVIEW'
    if (filter === 'approved') return report.status === 'APPROVED'
    return true
  })

  const stats = {
    pending: reports.filter(r => r.status === 'PENDING_REVIEW').length,
    approved: reports.filter(r => r.status === 'APPROVED').length,
    total: reports.length
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">👨‍⚕️ Worklist - Trưởng Khoa</h1>
        <p className="text-gray-600 mt-1">Danh sách phiếu kết quả cần phê duyệt</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="text-3xl font-bold text-yellow-700">{stats.pending}</div>
          <div className="text-sm text-yellow-600 mt-1">⏳ Chờ duyệt</div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-3xl font-bold text-green-700">{stats.approved}</div>
          <div className="text-sm text-green-600 mt-1">✅ Đã duyệt</div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="text-3xl font-bold text-gray-700">{stats.total}</div>
          <div className="text-sm text-gray-600 mt-1">📊 Tổng số phiếu</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-4 flex space-x-2">
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'pending'
              ? 'bg-yellow-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ⏳ Chờ duyệt ({stats.pending})
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'approved'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ✅ Đã duyệt ({stats.approved})
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-md font-medium ${
            filter === 'all'
              ? 'bg-gray-700 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 Tất cả ({stats.total})
        </button>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2">Đang tải...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">✅ Không có phiếu nào cần duyệt</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mã phiếu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bệnh nhân
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Người tạo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ngày tạo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/pathology/reports/${report.id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        {report.report_id}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {report.patient.full_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Mã BN: {report.patient.patient_id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {report.pathologist
                          ? `${report.pathologist.first_name} ${report.pathologist.last_name}`.trim() || report.pathologist.username
                          : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(report.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(report.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {report.status === 'PENDING_REVIEW' ? (
                        <div className="flex space-x-2">
                          <Link
                            to={`/pathology/cytology-reports/${report.id}`}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            👁️ Xem
                          </Link>
                          <button
                            onClick={() => handleApprove(report)}
                            disabled={actionLoading}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            ✅ Duyệt
                          </button>
                          <button
                            onClick={() => openRejectModal(report)}
                            disabled={actionLoading}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            ❌ Từ chối
                          </button>
                        </div>
                      ) : report.status === 'APPROVED' ? (
                        <div className="flex space-x-2">
                          <Link
                            to={`/pathology/cytology-reports/${report.id}`}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            👁️ Xem phiếu
                          </Link>
                          <Link
                            to={`/pathology/cytology-reports/${report.id}`}
                            target="_blank"
                            className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700"
                            title="Mở phiếu để in"
                          >
                            🖨️ In phiếu
                          </Link>
                        </div>
                      ) : (
                        <Link
                          to={`/pathology/cytology-reports/${report.id}`}
                          className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                        >
                          👁️ Xem
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                ❌ Từ Chối Phiếu Kết Quả
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Phiếu: <span className="font-medium">{selectedReport.report_id}</span> -
                BN: <span className="font-medium">{selectedReport.patient.full_name}</span>
              </p>
            </div>

            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lý do từ chối <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Nhập lý do từ chối phiếu kết quả..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Phiếu sẽ được chuyển về trạng thái "Nháp" để KTV sửa lại
              </p>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading || !rejectionReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Đang xử lý...' : '❌ Xác nhận từ chối'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ApprovalWorklist
